/**
 * Admin/InstallerStatus.tsx
 *
 * Shows whether the monday.com boards have been set up correctly,
 * which boards exist, and any errors from the last installer run.
 * Reads installationState.json from monday Storage key "installer_state".
 */

import { useEffect, useState } from 'react';
import { storageGet, storageSetJSON } from '@core/api/client';
import { checkWorkerHealth } from '@core/api/client';
import { useAppStore } from '@core/store/appStore';
import { discoverBoardsByName } from '@core/utils/boardSetup';
import { STORAGE_KEYS } from '@core/utils/defaults';
import type { InstallerStatus as TInstallerStatus } from '@core/types';

interface HealthState {
  checked: boolean;
  ok: boolean;
  tokenConfigured: boolean;
  message?: string;
}

export function InstallerStatus() {
  const [status, setStatus] = useState<TInstallerStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [health, setHealth] = useState<HealthState>({
    checked: false,
    ok: false,
    tokenConfigured: false,
  });
  const [discovering, setDiscovering] = useState(false);
  const [discoveryResult, setDiscoveryResult] = useState('');
  const { context, boardIds, setBoardIds } = useAppStore();
  const userId = (context?.user as Record<string, unknown> | undefined)?.id?.toString() ?? null;

  useEffect(() => {
    void loadStatus();
    void loadHealth();
  }, []);

  async function loadStatus() {
    setLoading(true);
    const stored = await storageGet<TInstallerStatus>('installer_state');
    setStatus(stored);
    setLoading(false);
  }

  async function loadHealth() {
    const result = await checkWorkerHealth();
    setHealth({ checked: true, ...result });
  }

  if (loading) {
    return (
      <div className="cos-admin-card">
        <p className="cos-admin-muted">Checking installation status…</p>
      </div>
    );
  }

  return (
    <div className="cos-admin-card">
      <h3 className="cos-admin-heading">Installation Status</h3>

      {/* Worker health */}
      <section className="cos-admin-section">
        <h4 className="cos-admin-subheading">Cloudflare Worker</h4>
        {!health.checked ? (
          <span className="cos-badge cos-badge--neutral">Checking…</span>
        ) : health.ok && health.tokenConfigured ? (
          <span className="cos-badge cos-badge--success">● Worker online · Token configured</span>
        ) : health.ok && !health.tokenConfigured ? (
          <span className="cos-badge cos-badge--warning">
            ⚠ Worker reachable but MONDAY_API_TOKEN missing — run{' '}
            <code>npm run worker:secret:token</code>
          </span>
        ) : (
          <span className="cos-badge cos-badge--error">
            ✗ Worker unreachable{health.message ? ` — ${health.message}` : ''}
          </span>
        )}
      </section>

      {/* Board setup */}
      <section className="cos-admin-section">
        <h4 className="cos-admin-subheading">monday.com Boards</h4>
        {!status ? (
          <p className="cos-admin-muted">
            No installer record found. Run{' '}
            <code>npm run monday:install</code> to set up your boards.
          </p>
        ) : (
          <>
            <div className="cos-admin-status-row">
              <span className="cos-admin-label">Last run</span>
              <span>
                {status.lastRunAt
                  ? new Date(status.lastRunAt).toLocaleString('en-ZA')
                  : '—'}
              </span>
            </div>

            <div className="cos-admin-status-row">
              <span className="cos-admin-label">Boards created</span>
              <span>{status.boardsCreated.length}</span>
            </div>

            {status.boardsCreated.length > 0 && (
              <ul className="cos-admin-list">
                {status.boardsCreated.map((b) => (
                  <li key={b} className="cos-admin-list-item cos-admin-list-item--success">
                    ✓ {b}
                  </li>
                ))}
              </ul>
            )}

            {status.missingBoards.length > 0 && (
              <>
                <div className="cos-admin-status-row cos-admin-status-row--warn">
                  <span className="cos-admin-label">Missing boards</span>
                  <span>{status.missingBoards.length}</span>
                </div>
                <ul className="cos-admin-list">
                  {status.missingBoards.map((b) => (
                    <li key={b} className="cos-admin-list-item cos-admin-list-item--warn">
                      ⚠ {b}
                    </li>
                  ))}
                </ul>
              </>
            )}

            {status.errors.length > 0 && (
              <details className="cos-admin-errors">
                <summary>
                  {status.errors.length} error{status.errors.length > 1 ? 's' : ''} from last run
                </summary>
                <pre className="cos-admin-pre">
                  {status.errors.join('\n')}
                </pre>
              </details>
            )}
          </>
        )}
      </section>

      {/* Board Discovery */}
      <section className="cos-admin-section">
        <h4 className="cos-admin-subheading">Board Discovery</h4>
        <p className="cos-admin-muted">
          Scan the workspace for Chanelle OS boards by name and populate the board ID map.
        </p>

        {discoveryResult && (
          <div className="cos-admin-banner cos-admin-banner--info">{discoveryResult}</div>
        )}

        {boardIds && Object.keys(boardIds).length > 0 && (
          <ul className="cos-admin-list">
            {Object.entries(boardIds)
              .filter(([, v]) => v)
              .map(([key, value]) => (
                <li key={key} className="cos-admin-list-item cos-admin-list-item--success">
                  {key}: {value}
                </li>
              ))}
          </ul>
        )}

        <button
          type="button"
          className="cos-btn cos-btn--secondary cos-btn--sm"
          disabled={discovering}
          onClick={async () => {
            if (!userId) {
              setDiscoveryResult('No user ID available.');
              return;
            }
            setDiscovering(true);
            setDiscoveryResult('Scanning workspace for COS boards…');
            try {
              const found = await discoverBoardsByName();
              if (found && found.actions) {
                setBoardIds(found);
                const key = STORAGE_KEYS.boardIds(userId);
                await storageSetJSON(key, found);
                const count = Object.values(found).filter(Boolean).length;
                setDiscoveryResult(`Found ${count} boards. Board IDs saved.`);
              } else {
                setDiscoveryResult(
                  'Could not find required boards (Topics, Notes, Actions). Ensure boards with "COS —" prefix exist.',
                );
              }
            } catch (err) {
              setDiscoveryResult(`Discovery failed: ${(err as Error).message}`);
            } finally {
              setDiscovering(false);
            }
          }}
        >
          {discovering ? 'Discovering…' : 'Discover Boards'}
        </button>
      </section>

      <button
        type="button"
        className="cos-btn cos-btn--secondary cos-btn--sm"
        onClick={() => void loadStatus()}
      >
        Refresh
      </button>
    </div>
  );
}
